<template>
  <v-container>
    <v-layout justify-center>
      <v-flex>
        <v-card>
          <v-app-bar dark color="primary">
            <h3>{{ $t('promoCodes.headline') }}</h3>
            <template v-slot:extension>
              <v-tabs
                v-model="tab"
                align-with-title
              >
                <v-tabs-slider color="white"></v-tabs-slider>
                <v-tab>{{ $t('promoCodes.tabNameFetched') }}</v-tab>
                <v-tab>{{ $t('promoCodes.tabNameActivated') }}</v-tab>
              </v-tabs>
            </template>
          </v-app-bar>
          <v-tabs-items v-model="tab">
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <div
                    v-if="loadingPromoBounties"
                    class="loading-container pa-6"
                  >
                    <v-progress-circular
                      indeterminate
                      color="primary"
                    />
                  </div>
                  <div
                    v-if="showPromoBountiesError"
                    class="error-container pa-6"
                  >
                    <p>
                      {{ $t('promoCodes.noPromoBountiesRedeemed') }}
                    </p>
                  </div>
                  <v-container
                    v-if="promoBounties.length > 0"
                    class="bounties-grid py-3"
                  >
                    <v-row>
                      <v-col
                        v-for="(promoBounty, index) in promoBounties"
                        :key="index"
                        :cols="6"
                        md="12"
                      >
                        <div
                          class="bounty-card"
                          @click="onBountyClick(promoBounty)"
                        >
                          <div
                            :class="[
                              'bounty-card-image-container',
                              { blank: !promoBounty.bounty.image }
                            ]"
                          >
                            <img
                              v-if="promoBounty.bounty.image"
                              :src="promoBounty.bounty.image"
                              alt=""
                              class="bounty-card-image"
                            />
                          </div>
                          <div class="bounty-card-info">
                            <p class="bounty-card-title info-column">
                              {{ promoBounty.bounty.name }}
                            </p>
                            <span class="bounty-card-meta info-column">
                              {{ $t('promoCodes.createdDateText', {
                                createdDate: promoBounty.formattedCreationDate
                              }) }}
                            </span>
                            <span
                              v-if="!promoBounty.expiration_date"
                              class="bounty-card-meta info-column"
                            >
                              {{ $t('promoCodes.promoCodeUnlimited') }}
                            </span>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-tab-item>
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <div
                    v-if="loadingActivatedBounties"
                    class="loading-container pa-6"
                  >
                    <v-progress-circular
                      indeterminate
                      color="primary"
                    />
                  </div>
                  <div
                    v-if="showActivatedBountiesError"
                    class="error-container pa-6"
                  >
                    <p>
                      {{ $t('promoCodes.noPromoBountiesActivated') }}
                    </p>
                  </div>
                  <v-container
                    v-if="activatedBounties.length > 0"
                    class="bounties-grid py-3"
                  >
                    <v-row>
                      <v-col
                        v-for="(promoBounty, index) in activatedBounties"
                        :key="index"
                        :cols="6"
                        md="12"
                      >
                        <div
                          class="bounty-card"
                          @click="onBountyClick(promoBounty)"
                        >
                          <div
                            :class="[
                              'bounty-card-image-container',
                              { blank: !promoBounty.bounty.image }
                            ]"
                          >
                            <img
                              v-if="promoBounty.bounty.image"
                              :src="promoBounty.bounty.image"
                              alt=""
                              class="bounty-card-image"
                            />
                          </div>
                          <div class="bounty-card-info">
                            <p class="bounty-card-title info-column">
                              {{ promoBounty.bounty.name }}
                            </p>
                            <span class="bounty-card-meta info-column">
                              {{ $t('promoCodes.expirationDateText', {
                                expirationDate: promoBounty.formattedExpirationDate
                              }) }}
                              &nbsp;
                              {{ promoBounty.isExpired
                                ? `(${$t('promoCodes.promoCodeExpired')})`
                                : ''
                              }}
                            </span>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-flex>
    </v-layout>
    <promo-dialog
      v-if="showPromoDialog"
      v-bind="{fetchedBounty, showPromoDialog}"
      @finish-pressed="onFinishPressed"
    />
  </v-container>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import {mapActions} from 'vuex'
import PromoDialog from '@/components/bounty/PromoDialog'

export default {
  name: 'PromoCodeList',
  components: {
    PromoDialog,
  },
  data() {
    return {
      tab: null,
      loadingPromoBounties: true,
      promoBounties: [],
      errorPromoBounties: false,
      loadingActivatedBounties: true,
      activatedBounties: [],
      errorActivatedBounties: false,
      fetchedBounty: null,
      showPromoDialog: false,
    }
  },
  computed: {
    showPromoBountiesError() {
      return !this.loadingPromoBounties
        && this.promoBounties.length === 0
    },
    showActivatedBountiesError() {
      return !this.loadingActivatedBounties
        && this.activatedBounties.length === 0
    },
  },
  mounted() {
    this.getPromoBounties()
    this.getActivatedBounties()
  },
  methods: {
    ...mapActions([
      'fetchPromoBounties',
      'fetchActivatedPromoBounties'
    ]),
    async getPromoBounties() {
      try {
        const bounties = await this.fetchPromoBounties()
        this.promoBounties = this.formatBounties(bounties)
        this.loadingPromoBounties = false
      } catch (error) {
        this.loadingPromoBounties = false
        this.errorPromoBounties = true
      }
    },
    async getActivatedBounties() {
      try {
        const bounties = await this.fetchActivatedPromoBounties()
        this.activatedBounties = this.formatBounties(bounties)
        this.loadingActivatedBounties = false
      } catch (error) {
        this.loadingActivatedBounties = false
        this.errorActivatedBounties = true
      }
    },
    formatBounties(bounties) {
      if (bounties.length > 0) {
        return bounties.map((promoBounty) => {
          const formattedCreationDate =
            moment(_.get(promoBounty, 'created')).format('DD.MM.YYYY')
          const expirationDate = _.get(promoBounty, 'expiration_date')
          const formattedExpirationDate =
            moment(expirationDate).format('DD.MM.YYYY HH:mm:ss')
          const hasExpirationDate = expirationDate
            && moment(expirationDate).isValid()
          const isExpired = hasExpirationDate
            && moment().isAfter(moment(expirationDate))

          return {
            ...promoBounty,
            formattedCreationDate,
            formattedExpirationDate,
            isExpired,
          }
        })
      }

      return []
    },
    onBountyClick(fetchedBounty) {
      this.fetchedBounty = fetchedBounty
      this.showPromoDialog = true
    },
    onFinishPressed() {
      this.showPromoDialog = false
    },
  },
}
</script>

<style lang="scss" scoped>
.loading-container {
  display: flex;
  justify-content: center;
  margin: 0 auto;
}

.error-container {
  text-align: center;
  margin: 0 auto;
}

.bounty-card {
  display: flex;
  width: 100%;
  flex-direction: column;
  cursor: pointer;

  @media(min-width: 960px) {
    flex-direction: row;
  }
}

.bounty-card-image-container {
  height: 140px;

  @media(min-width: 600px) {
    height: 200px;
  }

  @media(min-width: 960px) {
    flex-basis: 64px;
    flex-shrink: 0;
    height: 64px;
  }

  &.blank {
    background: linear-gradient(45deg, #3A434B, rgb(132, 148, 163)) ;
  }
}

.bounty-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bounty-card-info {
  padding-top: 16px;

  @media(min-width: 960px) {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: space-between;
    padding-top: 0;
    padding-left: 16px;
  }
}

.info-column {
  @media(min-width: 960px) {
    flex-basis: 33.33%;
    padding: 0 32px;
  }
}

.bounty-card-title {
  @media(min-width: 960px) {
    flex-basis: 42%;
    margin: 0;
  }
}

.bounty-card-meta {
  display: block;
}
</style>
